import {GetVehicleApiResponse} from '@dms/api/vehicle';
import {areFormFieldsDifferent, VehicleCustomFields} from '@dms/shared';

import {BaseVehicle} from '../types/BaseVehicle';

/**
 * Create customFields array for API request from form data
 * Handles German-specific fields (HSN, TSN, Umweltplakette) with proper type structure.
 * Only pushes the value into array if it's being changed. This function behaves like PATCH.
 *
 * @param formData - Vehicle form data
 * @param existingVehicleData - Existing(previous) vehicle data for reference purposes
 * @returns Array of custom field objects formatted for API
 *
 * @example
 * ```typescript
 * const customFields = createCustomFields({
 *   hsn: "1234",
 *   tsn: "abc123",
 *   umweltplakette: UmweltplaketteEnum.green
 * });
 * // Returns: [
 * //   {
 * //     definitionId: "manufacturerKeyNumber",
 * //     value: { type: "string", value: "1234" }
 * //   },
 * //   {
 * //     definitionId: "typeCodeNumber",
 * //     value: { type: "string", value: "ABC123" }
 * //   },
 * //   {
 * //     definitionId: "environmentalBadge",
 * //     value: { type: "string", value: "VEHICLEFIELD_CUSTOM_ENV_BADGE_GREEN" }
 * //   }
 * // ]
 * ```
 */
export const createCustomFields = (
  formData: BaseVehicle,
  existingVehicleData?: GetVehicleApiResponse
) => {
  const customFields = [];

  const existingHsn = existingVehicleData?.customFields?.find(
    (field) => field.definitionId === VehicleCustomFields.hsn.definitionId
  )?.value?.value;
  const existingTsn = existingVehicleData?.customFields?.find(
    (field) => field.definitionId === VehicleCustomFields.tsn.definitionId
  )?.value?.value;
  const existingUmweltplakette = existingVehicleData?.customFields?.find(
    (field) => field.definitionId === VehicleCustomFields.umweltplakette.definitionId
  )?.value?.value;

  if (formData.hsn !== undefined && areFormFieldsDifferent(formData.hsn, existingHsn)) {
    customFields.push({
      definitionId: VehicleCustomFields.hsn.definitionId,
      value:
        formData.hsn === null
          ? null
          : {
              type: VehicleCustomFields.hsn.valueType,
              value: formData.hsn,
            },
    });
  }

  if (formData.tsn !== undefined && areFormFieldsDifferent(formData.tsn, existingTsn)) {
    customFields.push({
      definitionId: VehicleCustomFields.tsn.definitionId,
      value:
        formData.tsn === null
          ? null
          : {
              type: VehicleCustomFields.tsn.valueType,
              value: formData.tsn.toUpperCase(), // Convert to uppercase per German standards
            },
    });
  }

  if (
    formData.umweltplakette !== undefined &&
    areFormFieldsDifferent(formData.umweltplakette, existingUmweltplakette)
  ) {
    customFields.push({
      definitionId: VehicleCustomFields.umweltplakette.definitionId,
      value:
        formData.umweltplakette === null
          ? null
          : {
              type: VehicleCustomFields.umweltplakette.valueType,
              value: formData.umweltplakette,
            },
    });
  }

  return customFields;
};
